import React, { useState } from "react";

/**
 * A reusable table component for displaying and editing parameter inputs.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.parameters - Array of parameter input objects
 * @param {Object} props.values - Current values for parameters
 * @param {Function} props.onValueChange - Function to call when a value changes
 * @param {Function} props.formatValue - Optional function to format display values
 */
const ParameterTable = ({ parameters, values, onValueChange, formatValue = (val) => val }) => {
  // Track raw input values before parsing to numbers
  const [inputValues, setInputValues] = useState({});
  
  // Format currency with thousands separator and no decimals
  const defaultFormatCurrency = (value) => {
    return Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Handle input change
  const handleInputChange = (id, value) => {
    // Allow numeric values, decimal points, and empty strings
    const isValidInput = /^[0-9]*\.?[0-9]*$/.test(value) || value === '';
    
    if (isValidInput) {
      // Store the raw input first
      setInputValues(prev => ({
        ...prev,
        [id]: value
      }));
      
      // Convert to number for the actual parameter
      const paramValue = value === '' ? 0 : parseFloat(value);
      
      // Only update if it's a valid number
      if (!isNaN(paramValue)) {
        onValueChange(id, paramValue);
      }
    }
  };

  // Get the displayed value for an input
  const getDisplayValue = (input, currentValue) => {
    if (inputValues[input.id] !== undefined) return inputValues[input.id];
    return input.id.includes('price') ? defaultFormatCurrency(currentValue) : currentValue.toString();
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Parameter</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Value</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-auto">Description</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {parameters.map((input) => {
            const currentValue = values?.[input.id] ?? input.defaultValue;
            const isPrice = input.id.includes('price');
            return (
              <tr key={input.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-800">{input.label}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="relative rounded-md shadow-sm">
                    {isPrice && <div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center"><span className="text-gray-500 sm:text-sm">$</span></div>}
                    <input
                      type="text"
                      value={getDisplayValue(input, currentValue)}
                      onChange={(e) => handleInputChange(input.id, e.target.value)}
                      className={`w-full block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${isPrice ? 'pl-7' : 'pl-3'}`}
                    />
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">{input.description}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ParameterTable; 